/**
 * The red bar across the top of every screen while an admin is impersonating
 * someone (ADR impersonation). Impossible to miss, so nobody forgets whose account they
 * are acting in. Ends the impersonation by itself when the hour runs out.
 */
import { CircleAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { impersonationNotice } from '@/core/impersonation'
import { useImpersonation } from '../api'

export function ImpersonationBar({ name, expiresAt }: { name: string; expiresAt: string }) {
  const { stop, busy } = useImpersonation()

  // The server ends the session at the hour; take the admin straight back
  // rather than leave them looking at a signed-out screen.
  useEffect(() => {
    const ms = new Date(expiresAt).getTime() - Date.now()
    const timer = setTimeout(() => void stop(), Math.max(ms, 0))
    return () => clearTimeout(timer)
  }, [expiresAt, stop])

  return (
    // Opaque base, red tint on top: content scrolling underneath never shows
    // through the bar.
    <div
      role="status"
      data-slot="impersonation-bar"
      // Pinned by the shell, which stacks it under the Sandbox strip when
      // there is one (src/components/app/app-shell.tsx).
      className="h-11 shrink-0 bg-background"
    >
      <div className="flex h-full items-center gap-3 border-b border-destructive/30 bg-destructive/10 px-4 text-destructive-strong">
        <CircleAlert className="size-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold">
          <span className="md:hidden">Impersonating {name}</span>
          <span className="hidden md:inline">{impersonationNotice(name)}</span>
        </p>
        <Button
          variant="destructive-solid"
          size="sm"
          onClick={() => void stop()}
          disabled={busy}
          aria-label="Stop impersonating"
          className="shrink-0"
        >
          {busy ? (
            'Stopping…'
          ) : (
            <>
              Stop<span className="hidden md:inline"> impersonating</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
